import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import * as permissionApi from '@/api/permissionApi';
import { useAuthStore } from './useAuthStore';

/**
 * 权限状态管理Store
 */
export const usePermissionStore = defineStore('permission', () => {
  // 状态
  const permissions = ref<Set<string>>(new Set());
  const matrixLeaves = ref<Set<string>>(new Set());
  const roles = ref<Set<string>>(new Set());
  const loading = ref(false);
  const error = ref<string | null>(null);
  const lastFetchTime = ref<number>(0);
  
  // 缓存时间（5分钟）
  const CACHE_DURATION = 5 * 60 * 1000;

  // 计算属性
  const hasPermissions = computed(() => {
    return (permissionCodes: string | string[], requireAll = false): boolean => {
      if (!permissionCodes || (Array.isArray(permissionCodes) && permissionCodes.length === 0)) {
        return false;
      }
      
      const codes = Array.isArray(permissionCodes) ? permissionCodes : [permissionCodes];
      
      if (requireAll) {
        // 要求所有权限
        return codes.every(code => permissions.value.has(code));
      } else {
        // 满足任意一个即可
        return codes.some(code => permissions.value.has(code));
      }
    };
  });

  const hasRoles = computed(() => {
    return (roleCodes: string | string[], requireAll = false): boolean => {
      if (!roleCodes || (Array.isArray(roleCodes) && roleCodes.length === 0)) {
        return false;
      }
      
      const codes = Array.isArray(roleCodes) ? roleCodes : [roleCodes];
      
      if (requireAll) {
        return codes.every(code => roles.value.has(code));
      } else {
        return codes.some(code => roles.value.has(code));
      }
    };
  });

  function checkMatrixPermission(leafCode: string): boolean {
    // 子管理员：matrixLeaves 可为 *，但仍禁止「设置管理员」叶子
    const subAdminExcluded = new Set([
      'organization-matrix-8-1',
      'organization-matrix-8-3',
    ]);
    if (
      subAdminExcluded.has(leafCode) &&
      roles.value.has('ADMIN') &&
      !roles.value.has('SUPER_ADMIN')
    ) {
      return false;
    }
    if (matrixLeaves.value.has('*')) {
      return true;
    }
    if (matrixLeaves.value.has(leafCode)) {
      return true;
    }
    return permissions.value.has(leafCode);
  }

  /**
   * 检查权限（带缓存）
   */
  function checkPermission(permissionCode: string): boolean {
    return permissions.value.has(permissionCode);
  }

  /**
   * 检查角色（带缓存）
   */
  function checkRole(roleCode: string): boolean {
    return roles.value.has(roleCode);
  }

  /**
   * 获取用户权限列表
   */
  async function fetchPermissions(): Promise<void> {
    const authStore = useAuthStore();
    
    if (!authStore.isAuthenticated) {
      permissions.value.clear();
      roles.value.clear();
      matrixLeaves.value.clear();
      return;
    }

    // 检查缓存是否有效
    const now = Date.now();
    if (now - lastFetchTime.value < CACHE_DURATION && permissions.value.size > 0) {
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await permissionApi.getUserPermissions();
      permissions.value = new Set(response.permissions);
      matrixLeaves.value = new Set(response.matrixLeaves ?? []);
      roles.value = new Set(response.roles);
      lastFetchTime.value = now;
      // 同步角色到 authStore，供全局使用
      authStore.setRoles(response.roles ?? []);
    } catch (err: any) {
      error.value = err.message || '获取权限失败';
      console.error('获取权限失败:', err);
      // 如果获取失败，可能是Token过期，清除权限
      if (err.status === 401) {
        permissions.value.clear();
        roles.value.clear();
        matrixLeaves.value.clear();
        authStore.setRoles([]);
      }
    } finally {
      loading.value = false;
    }
  }

  /**
   * 清除权限缓存
   */
  function clearCache(): void {
    permissions.value.clear();
    roles.value.clear();
    matrixLeaves.value.clear();
    lastFetchTime.value = 0;
    useAuthStore().setRoles([]);
  }

  /**
   * 刷新权限（强制重新获取）
   */
  async function refreshPermissions(): Promise<void> {
    clearCache();
    await fetchPermissions();
  }

  /**
   * 设置权限（用于测试或手动设置）
   */
  function setPermissions(permissionList: string[], roleList: string[]): void {
    permissions.value = new Set(permissionList);
    matrixLeaves.value = new Set(permissionList.filter(code => code.includes('-matrix-')));
    roles.value = new Set(roleList);
    lastFetchTime.value = Date.now();
  }

  return {
    // 状态
    permissions: computed(() => Array.from(permissions.value)),
    matrixLeaves: computed(() => Array.from(matrixLeaves.value)),
    roles: computed(() => Array.from(roles.value)),
    loading,
    error,
    // 计算属性
    hasPermissions,
    hasRoles,
    // 方法
    checkPermission,
    checkMatrixPermission,
    checkRole,
    fetchPermissions,
    clearCache,
    refreshPermissions,
    setPermissions,
  };
});
